/* The new-effort form's words (designs/intake-form-help.md FH1): the page
   intro, a short visible hint and a longer hover for every field, a hover per
   Affects choice and per button. One reviewed file, each entry citing the
   decision it paraphrases (FH6: the words describe what the form does, and
   never promise an owner or an outcome it does not decide). The process
   steps are NOT here: the ⓘ reads them from INTAKE_ABOUT_MODULE (FH3). */
import type { SCOPES } from "../../../shared/capabilities";

export interface FieldHelp {
  /* one short sentence, shown under the field and read out on focus */
  hint: string;
  /* the reason, on the label's hover */
  more: string;
}

export type FormField =
  | "team"
  | "product"
  | "vehicle"
  | "name"
  | "description"
  | "contact"
  | "inProgress"
  | "workingTeam"
  | "neededBy"
  | "scope"
  | "jama";

type ScopeValue = (typeof SCOPES)[number]["value"];

/* the page head's line (FH2) — intake-about-module.ts INTRO, condensed */
export const FORM_INTRO =
  "Put a large piece of engineering work on the record, so the org can see it and two teams " +
  "don't build the same thing. You don't need to know which platform team owns it: the platform " +
  "managers route it when they groom.";

export const FORM_HELP: Readonly<Record<FormField, FieldHelp>> = {
  /* UF15 + teams-and-products.md TP1/TP5: pick-or-add over the Hermes list,
     grouped by kind, near matches first; not Entra; an addition waits for
     an Admin on the Teams section */
  team: {
    hint: "Your team — a platform, product or other team. Pick it, or type a new name and add it.",
    more:
      "The team registering this effort, from Hermes's own team list, grouped into platform " +
      "teams, product teams (one per PDP, like Dozer or Telehandler) and other teams (customer " +
      "programs and prototype work, like Blitz or Scythe). Pick your existing entry rather than " +
      "adding a variant of it: the duplicate check and +1s count teams, so two names for one " +
      "team split its backing. A team you add waits for an Admin to file it on Teams.",
  },
  /* TP2/TP8: products of product and other teams (several each), grouped by team; pick-only */
  product: {
    hint: "The product it's for, grouped by the team that owns it. Optional — leave it blank if there isn't one.",
    more:
      "Each product belongs to a team (platform teams own platform products) — a team may own several, each its own PDP. If yours " +
      "is not listed, an Admin has not added it yet — pick your team above and say so in What / why. Left blank, the product is recorded as not recorded rather than " +
      "guessed.",
  },
  /* TP4: a product's vehicles, pick-or-add, optional */
  vehicle: {
    hint: "Which of that product's vehicles. Optional — leave it blank if it's all of them or you're not sure.",
    more:
      "A product can have several vehicles (Ag has the 8R and the 9R). Only this product's " +
      "vehicles are listed. A vehicle you add waits for an Admin to review it.",
  },
  /* capabilities.ts similarityRanked: the name counts twice */
  name: {
    hint: "A short title someone on another team would recognise.",
    more:
      "The name the queue shows. The duplicate check weighs it most heavily, so name the thing " +
      "itself (\"Dust-robust obstacle detection\") rather than a project code.",
  },
  /* the placeholder's plain-words rule; the description counts once */
  description: {
    hint: "Write it for someone outside your team — the duplicate check reads this too.",
    more:
      "What breaks or is needed, on which vehicle, and why, in plain words. Concrete words (the " +
      "sensor, the vehicle, the failure) help the duplicate check find an effort that is already " +
      "on the register.",
  },
  /* UF16 + the use case: a person, required, any team; not a platform role.
     "Lane lead" is Trevor's word (Sep 14 2026), so the hint defines it. The
     requirement itself is FORM_MISSING's job, not repeated here (FH8). */
  contact: {
    hint: "The person leading this effort, its lane lead: who platform and other teams talk to about it. Anyone, from any team.",
    more:
      "When another team needs the same thing, this is the person platform puts them in touch " +
      "with. Picked from the company roster. It is a person on the effort, not a platform role.",
  },
  /* UF20 + the use case: a requester's owner is a suggestion until groomed */
  inProgress: {
    hint: "If a team is already building it, say which team.",
    more:
      "Your answer is a suggestion until a platform manager grooms the effort and names who " +
      "builds it. Leave it as Not in progress if nobody has started.",
  },
  /* UF20: the same team list as Team */
  workingTeam: {
    hint: "The team doing the work now. Same list as Team.",
    more:
      "Recorded as a suggestion, shown in orange on the queue, until a platform manager grooms " +
      "the effort and confirms the owner.",
  },
  /* settled decision #2: a blank is not recorded, never guessed */
  neededBy: {
    hint: "The date you need it by. Optional — leave it blank rather than guess.",
    more: "A real deadline helps grooming. Left blank, it is recorded as not recorded.",
  },
  /* capabilities.ts SCOPES; "unsure" is an honest answer of its own */
  scope: {
    hint: "One vehicle, or likely several? Not sure is a real answer.",
    more:
      "Whether this need is likely to be shared across vehicles. Several vehicles is a hint to " +
      "platform that it may be worth building once; Not sure flags it for coordination.",
  },
  /* the record keeps the link; nothing checks it against Jama */
  jama: {
    hint: "A link to the Jama item, if requirements already exist. Optional — leave it blank if not.",
    more: "Paste the full link. Hermes records it with the effort; it does not read Jama.",
  },
};

/* the red line a required person field shows while it is empty (FH8). On the
   new form nothing is recorded yet, so it is an instruction; "Not recorded"
   is the honest word only on a saved effort (the edit form says that). */
export const FORM_MISSING = {
  contact: "Required — pick someone before you register.",
} as const;

export const SCOPE_HELP: Readonly<Record<ScopeValue, string>> = {
  common: "The need probably applies to more than one vehicle — a candidate to build once.",
  specific: "The need belongs to one vehicle.",
  unsure: "You can't tell yet. The effort is flagged for coordination rather than guessed at.",
};

/* UF4 / intake-capture.md: the submit-time check and its two ways out */
export const BUTTON_HELP = {
  register:
    "Checks the register for similar efforts first. If any are found, you choose: +1 one of them, " +
    "or register yours anyway.",
  notDuplicate: "Register yours as a separate effort. The similar ones stay as they are.",
  plusOne: "Back this effort as your team instead of filing a second copy. Your +1 raises its reach.",
} as const;

const blank = (s: string): boolean => s.trim() === "";

/* the registry's own check (FH1) — takes the record as a parameter so its
   refusals can be exercised, the moduleAboutReason idiom */
export const formHelpReason = (
  h: {
    FORM_HELP: Readonly<Record<FormField, FieldHelp>>;
    SCOPE_HELP: Readonly<Record<ScopeValue, string>>;
    BUTTON_HELP: Readonly<Record<keyof typeof BUTTON_HELP, string>>;
    FORM_INTRO: string;
  } = { FORM_HELP, SCOPE_HELP, BUTTON_HELP, FORM_INTRO },
): string | null => {
  if (blank(h.FORM_INTRO)) return "the page intro is blank";
  if (blank(FORM_MISSING.contact)) return "the missing-contact line is blank";
  for (const [k, f] of Object.entries(h.FORM_HELP)) {
    if (blank(f.hint)) return `field ${k} has a blank hint`;
    if (blank(f.more)) return `field ${k} has a blank hover`;
  }
  for (const [k, t] of Object.entries(h.SCOPE_HELP)) if (blank(t)) return `Affects choice ${k} has a blank hover`;
  for (const [k, t] of Object.entries(h.BUTTON_HELP)) if (blank(t)) return `button ${k} has a blank hover`;
  return null;
};
